import IResult from '../../models/result/IResult';
import { AssistantLaunchRecord, RevenueReportResponse } from '../../models/revenue/AssistantLaunchReport';
import { VOICIFY_ACCESS_TOKEN_STORE } from '../../constants/Keys';
import { sendGet } from './generic';

type RevenueReportData = RevenueReportResponse | AssistantLaunchRecord[];
type RevenueCacheKey = 'report' | 'projections';

interface RevenueCacheEntry {
    accessToken: string | null;
    expiresAt: number;
    result?: IResult<RevenueReportData>;
    request?: Promise<IResult<RevenueReportData>>;
}

const REVENUE_CACHE_TTL_MS = 5 * 60 * 1000;
const revenueCache: Partial<Record<RevenueCacheKey, RevenueCacheEntry>> = {};

const getAccessToken = () => sessionStorage.getItem(VOICIFY_ACCESS_TOKEN_STORE);

const getCachedRevenueReport = (cacheKey: RevenueCacheKey, load: () => Promise<IResult<RevenueReportData>>): Promise<IResult<RevenueReportData>> => {
    const accessToken = getAccessToken();
    const cached = revenueCache[cacheKey];

    if (cached?.accessToken === accessToken) {
        if (cached.result && cached.expiresAt > Date.now()) return Promise.resolve(cached.result);
        if (cached.request) return cached.request;
    }

    const entry: RevenueCacheEntry = { accessToken, expiresAt: 0 };
    entry.request = load().then(result => {
        entry.request = undefined;
        if (result.resultType === 'Ok') {
            entry.result = result;
            entry.expiresAt = Date.now() + REVENUE_CACHE_TTL_MS;
        } else if (revenueCache[cacheKey] === entry) {
            delete revenueCache[cacheKey];
        }
        return result;
    });
    revenueCache[cacheKey] = entry;
    return entry.request;
};

// These endpoints must authorize the signed-in user on the API. Do not use a
// reporting API key in the browser: it would be visible to every Spark visitor.
export const getRevenueReport = (): Promise<IResult<RevenueReportData>> => getCachedRevenueReport(
    'report',
    () => sendGet<RevenueReportData>('/revenuereport')
);

export const getRevenueReportProjections = (): Promise<IResult<RevenueReportData>> => getCachedRevenueReport(
    'projections',
    async () => {
        const result = await sendGet<RevenueReportData>('/revenuereport/projections');
        // Preserve compatibility with deployments that expose the shortened route.
        return result.resultType === 'NotFound'
            ? sendGet<RevenueReportData>('/revenue/projections')
            : result;
    }
);

// The API filters by target launch date and enforces the caller's organization
// permissions before returning lightweight application/organization IDs.
export const getScheduledAssistantLaunches = (month: string): Promise<IResult<AssistantLaunchRecord[]>> => {
    return sendGet<AssistantLaunchRecord[]>(`/application/scheduled-launches?month=${encodeURIComponent(month)}`);
};
